import { useEffect, useMemo, useState } from 'react';
import {
  Handshake, Plus, Search, Pencil, Ban, CheckCircle2, Loader2,
  AlertCircle, Mail, Clock, RefreshCw, QrCode, Link2,
} from 'lucide-react';
import {
  listAgents, updateAgent, resendAgentInvite, copyAgentInviteLink,
  getDisplayErrorMessage,
  deriveInviteStatus,
  type AgentRow,
} from '@/lib/api';
import { INVITE_LINK_COPY_EN, partnerInviteOpen } from '@/lib/inviteLink';
import { useInviteLinkDialog } from '@/components/ui/InviteLinkDialog';
import { Modal } from '@/components/ui/Modal';
import { ResponsiveTable, type Column } from '@/components/ui/ResponsiveTable';
import formStyles from '@/components/ui/Form.module.css';
import {
  CONNECT_STATE_LABELS, DEFAULT_PARTNER_FILTERS, activeFilterCount, connectStateOf, filterPartners, repOptions,
  type PartnerConnectState, type PartnerFilters,
} from '@/lib/partnerDirectory';
import { RegionChips, RegionPicker } from '@/components/regions/RegionPicker';
import { AgentFormModal } from './AgentFormModal';
import { RecruitPartnerCard, useRecruitLinks } from './RecruitPartnerCard';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { StaffPartnerNotes } from '@/pages/sales-team/PartnerNotes';
import styles from './AgentsListPage.module.css';
import dir from './PartnerDirectory.module.css';

/**
 * Sales Partners (Partners > Sales Partners; Agents in the codebase).
 *
 * The directory Warren asked for on 24 October 2026: every partner, with
 * search and filters like Drivers, each row opening the partner's profile
 * (/agents/:id), and a "Recruit a Sales Partner" card with a QR code and
 * link a prospect uses to apply.
 *
 * GET /admin/agents returns the whole rolodex (dozens of partners), so search
 * and filters run in memory (lib/partnerDirectory.ts). Every control is gated
 * on what the Api says this viewer may do: row actions on the row's can_edit
 * (a verified Super Admin), the recruit card on its own read succeeding (the
 * Super Admin check that also issues a link), and Add on either.
 *
 * Regions (25 October 2026): each partner's states and provinces show in the
 * Regions column, and the region filter keeps partners serving any of the
 * regions picked. Every viewer who sees the list sees its regions.
 */
interface AgentsListPageProps {
  /** SPA navigation callback. Optional so legacy direct-render call
   *  sites don't break; missing → name anchor falls back to default
   *  href (full page reload). */
  onNavigate?: (to: string) => void;
}

const CONNECT_FILTERS: PartnerConnectState[] = ['ready', 'pending', 'restricted', 'unchecked', 'not_started'];

export function AgentsListPage({ onNavigate }: AgentsListPageProps = {}) {
  useRecentRoute('Sales Partners');

  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState<PartnerFilters>(DEFAULT_PARTNER_FILTERS);
  const [recruitOpen, setRecruitOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<AgentRow | null>(null);
  const [confirmAction, setConfirmAction] = useState<
    { agent: AgentRow; kind: 'deactivate' | 'reactivate' } | null
  >(null);
  const [confirmPending, setConfirmPending] = useState(false);
  const [confirmErr, setConfirmErr] = useState('');
  // Per-row invite resend (parity with the Team page's Re-send action). The
  // endpoint is audited and Super Admin gated server-side.
  const [resendingId, setResendingId] = useState<string | null>(null);
  // Copy invite link beside it (29 October 2026): a fresh link shown once in a dialog.
  const inviteLink = useInviteLinkDialog();

  const recruitLinks = useRecruitLinks();
  const canRecruit = recruitLinks.isSuccess;
  // POST /admin/agents and the recruit link read make the same verified
  // Super Admin check; a row's can_edit is that check too.
  const canManage = canRecruit || agents.some((a) => a.can_edit === true);

  const handleResendInvite = async (agent: AgentRow): Promise<void> => {
    if (resendingId) return;
    setResendingId(agent.id);
    setError('');
    try {
      const refreshed = await resendAgentInvite(agent.id);
      setAgents((prev) => prev.map((x) => (x.id === refreshed.id ? { ...x, ...refreshed } : x)));
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'Failed to re-send the invite.'));
    } finally {
      setResendingId(null);
    }
  };

  const reload = async (): Promise<void> => {
    setLoading(true);
    setError('');
    try {
      setAgents(await listAgents());
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'Failed to load Sales Partners'));
    } finally {
      setLoading(false);
    }
  };

  // Single fetch on mount; the server is the access boundary (a viewer it
  // refuses sees its message). reload re-runs after a save.
  useEffect(() => {
    void reload();
    // reload is a stable closure on this component; intentionally
    // not in the dep array (would loop on every render).
  }, []);

  const visible = useMemo(() => filterPartners(agents, filters), [agents, filters]);
  const reps = useMemo(() => repOptions(agents), [agents]);
  // The rep filter only when the Api sent rep data (Super Admin rows).
  const showRepFilter = agents.some((a) => a.sales_staff_id !== undefined && a.can_edit === true);
  const filtersActive = activeFilterCount(filters) > 0 || filters.search.trim() !== '';
  const set = <K extends keyof PartnerFilters>(key: K, value: PartnerFilters[K]) => setFilters((f) => ({ ...f, [key]: value }));
  const go = (to: string) => (e: React.MouseEvent) => {
    if (onNavigate) { e.preventDefault(); onNavigate(to); }
  };

  const handleToggleActive = async (): Promise<void> => {
    if (!confirmAction) return;
    setConfirmPending(true);
    setConfirmErr('');
    try {
      await updateAgent(confirmAction.agent.id, {
        active: confirmAction.kind === 'reactivate',
      });
      await reload();
      setConfirmAction(null);
    } catch (err) {
      setConfirmErr(await getDisplayErrorMessage(err, 'Failed to update Sales Partner'));
    } finally {
      setConfirmPending(false);
    }
  };

  const columns: Column<AgentRow>[] = [
    {
      key: 'name', header: 'Name', mobile: 'primary', sortable: true, sortKey: (a) => a.name.toLowerCase(),
      cell: (a) => (
        <span className={dir.stackStart}>
          <a href={`/agents/${a.id}`} className={dir.nameLink} onClick={go(`/agents/${a.id}`)}>{a.name}</a>
          {a.contact_name && a.contact_name !== a.name && <span className={dir.muted}>{a.contact_name}</span>}
        </span>
      ),
    },
    { key: 'email', header: 'Email', mobile: 'secondary', cell: (a) => <span className={`${dir.muted} ${dir.wrapAnywhere}`}>{a.email}</span> },
    {
      key: 'program', header: 'Program', mobile: 'body',
      cell: (a) => (
        <span className={dir.stackStart}>
          <span className={a.program_info?.program === 'golden' ? `${dir.chip} ${dir.chipGold}` : undefined}>{a.program_info?.name ?? 'Sales Partner'}</span>
          {a.program_info?.pending.map((p) => <small key={p.effective_at} className={dir.muted}>Scheduled: {p.program === 'golden' ? 'Golden Partner' : 'Sales Partner'}</small>)}
        </span>
      ),
    },
    {
      key: 'regions', header: 'Regions', mobile: 'body',
      cell: (a) => <RegionChips codes={a.served_regions} empty="Not set" />,
    },
    {
      // Activity pace (EVI_SP_PROGRAM_SPEC, V1 visibility): pay-to-use
      // chargers deployed in the trailing window vs the minimum. Only
      // meaningful after qualification.
      key: 'pace', header: 'Pace', mobile: 'hidden',
      cell: (a) => {
        const act = a.activity;
        if (!act) return <span className={dir.muted}>Not available</span>;
        if (!a.qualified_at) return <span className={dir.muted} title="Activity requirement starts at qualification (4 pay-to-use chargers).">Pre-qual</span>;
        const title = `${act.deployed_last_window} of ${act.min} pay-to-use chargers in the last 365 days`
          + (act.on_pace ? ' (on pace)' : ` (behind by ${act.behind_by})`);
        return (
          <span className={`${dir.chip} ${act.on_pace ? dir.chipOk : dir.chipWarn} ${dir.num}`} title={title}>
            {act.on_pace ? <CheckCircle2 size={11} /> : <AlertCircle size={11} />}
            {act.deployed_last_window}/{act.min}
          </span>
        );
      },
    },
    {
      key: 'connect', header: 'Payouts', mobile: 'body',
      cell: (a) => {
        const state = connectStateOf(a);
        const tone = state === 'ready' ? dir.chipOk : state === 'restricted' ? dir.chipErr : state === 'not_started' ? '' : dir.chipWarn;
        return <span className={`${dir.chip} ${tone}`}>{CONNECT_STATE_LABELS[state]}</span>;
      },
    },
    {
      // Re-derived client-side so a list left open across the 21 day expiry
      // flips Pending to Expired without a refetch.
      key: 'invite', header: 'Invite', mobile: 'body',
      cell: (a) => {
        const status = deriveInviteStatus(a);
        if (status === 'accepted') return <span className={`${dir.chip} ${dir.chipOk}`}><CheckCircle2 size={11} /> Accepted</span>;
        if (status === 'expired') return <span className={`${dir.chip} ${dir.chipWarn}`}><Clock size={11} /> Expired</span>;
        return <span className={`${dir.chip} ${dir.chipInfo}`}><Mail size={11} /> Pending</span>;
      },
    },
    ...(showRepFilter ? [{
      key: 'rep', header: 'Sales rep', mobile: 'body' as const,
      cell: (a: AgentRow) => (a.sales_rep_name ? <span>{a.sales_rep_name}</span> : <span className={dir.muted}>House book</span>),
    }] : []),
    {
      key: 'clawback', header: 'Clawback', mobile: 'hidden', align: 'right',
      cell: (a) => (a.outstanding_clawback_cents > 0
        ? <span className={`${dir.chip} ${dir.chipWarn} ${dir.num}`}>${(a.outstanding_clawback_cents / 100).toFixed(2)}</span>
        : <span className={`${dir.muted} ${dir.num}`}>$0.00</span>),
    },
    {
      key: 'status', header: 'Status', mobile: 'body',
      cell: (a) => (a.active ? <span className={styles.pillActive}>Active</span> : <span className={styles.pillInactive}>Inactive</span>),
    },
    {
      key: 'actions', header: <span className={dir.muted}>Actions</span>, mobile: 'body', align: 'right',
      cell: (a) => (a.can_edit === true ? (
        <span className={dir.rowActions}>
          <button type="button" className={dir.iconBtn} title="Edit" aria-label={`Edit ${a.name}`} onClick={() => setEditingAgent(a)}>
            <Pencil size={13} />
          </button>
          {/* Re-send invite and Copy invite link (29 October 2026), parity with
              the Team page: an active partner whose invite is still outstanding
              (pending or expired). One rule for both (lib/inviteLink.ts), and
              both endpoints run the same Super Admin gate. */}
          {partnerInviteOpen(a) && (
            <>
              <button type="button" className={dir.iconBtn} title="Re-send invite" aria-label={`Re-send invite to ${a.name}`}
                onClick={() => void handleResendInvite(a)} disabled={resendingId === a.id}>
                {resendingId === a.id ? <Loader2 size={13} className={styles.spin} /> : <RefreshCw size={13} />}
              </button>
              <button type="button" className={dir.iconBtn} title={INVITE_LINK_COPY_EN.action}
                aria-label={`${INVITE_LINK_COPY_EN.action} for ${a.name}`} disabled={inviteLink.busyFor === a.email}
                onClick={() => void inviteLink.open({ email: a.email, name: a.contact_name || a.name }, () => copyAgentInviteLink(a.id))}>
                {inviteLink.busyFor === a.email ? <Loader2 size={13} className={styles.spin} /> : <Link2 size={13} />}
              </button>
            </>
          )}
          <button type="button" className={dir.iconBtn} title={a.active ? 'Deactivate' : 'Reactivate'}
            aria-label={`${a.active ? 'Deactivate' : 'Reactivate'} ${a.name}`}
            onClick={() => setConfirmAction({ agent: a, kind: a.active ? 'deactivate' : 'reactivate' })}>
            {a.active ? <Ban size={13} /> : <CheckCircle2 size={13} />}
          </button>
        </span>
      ) : <span className={dir.muted}>View only</span>),
    },
  ];

  return (
    <div className={dir.page}>
      <div className={styles.header}>
        <div className={styles.headerIcon}><Handshake size={18} /></div>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>Sales Partners</h1>
          <p className={styles.subtitle}>
            Every Sales Partner, their invite and payout setup. Open a partner for their application,
            jobs, leads, chargers, commissions, statements and activity. Assign partners to chargers via
            the Network Origin section on each charger&rsquo;s detail page.
          </p>
        </div>
        <div className={dir.headerActions}>
          {canRecruit && (
            <button type="button" className={`${dir.secondaryBtn} ${recruitOpen ? dir.secondaryBtnActive : ''}`}
              onClick={() => setRecruitOpen((v) => !v)} aria-expanded={recruitOpen}>
              <QrCode size={14} /> Recruit a Sales Partner
            </button>
          )}
          {canManage && (
            <button type="button" className={styles.createBtn} onClick={() => setCreateOpen(true)} disabled={loading}>
              <Plus size={14} /> Add Sales Partner
            </button>
          )}
        </div>
      </div>

      {canRecruit && recruitOpen && <RecruitPartnerCard onClose={() => setRecruitOpen(false)} />}

      {!error && (
        <div className={dir.filterRow}>
          <div className={dir.searchWrap}>
            <Search size={14} className={dir.searchIcon} />
            <input
              className={dir.searchInput}
              type="search"
              placeholder="Search name, email, phone, company or code"
              aria-label="Search Sales Partners"
              value={filters.search}
              onChange={(e) => set('search', e.target.value)}
            />
          </div>
          <select className={dir.filterSelect} aria-label="Filter by status" value={filters.status}
            onChange={(e) => set('status', e.target.value as PartnerFilters['status'])}>
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select className={dir.filterSelect} aria-label="Filter by program" value={filters.program}
            onChange={(e) => set('program', e.target.value as PartnerFilters['program'])}>
            <option value="all">All programs</option>
            <option value="standard">Sales Partner</option>
            <option value="golden">Golden Partner</option>
          </select>
          <select className={dir.filterSelect} aria-label="Filter by invite" value={filters.invite}
            onChange={(e) => set('invite', e.target.value as PartnerFilters['invite'])}>
            <option value="all">Any invite</option>
            <option value="pending">Invite pending</option>
            <option value="expired">Invite expired</option>
            <option value="accepted">Invite accepted</option>
          </select>
          <select className={dir.filterSelect} aria-label="Filter by payout setup" value={filters.connect}
            onChange={(e) => set('connect', e.target.value as PartnerFilters['connect'])}>
            <option value="all">Any payout setup</option>
            {CONNECT_FILTERS.map((s) => <option key={s} value={s}>Payouts: {CONNECT_STATE_LABELS[s]}</option>)}
          </select>
          {showRepFilter && (
            <select className={dir.filterSelect} aria-label="Filter by sales rep" value={filters.rep}
              onChange={(e) => set('rep', e.target.value)}>
              <option value="all">Any sales rep</option>
              <option value="house">House book</option>
              {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          )}
          <RegionPicker value={filters.regions} onChange={(next) => set('regions', next)} label="Filter by region" emptyLabel="Any region"
            className={dir.regionFilter} />
          {filtersActive && (
            <button type="button" className={dir.clearBtn} onClick={() => setFilters(DEFAULT_PARTNER_FILTERS)}>Clear filters</button>
          )}
        </div>
      )}

      {loading && (
        <div className={styles.empty}>
          <Loader2 size={18} className={styles.spin} />
          <span>Loading Sales Partners…</span>
        </div>
      )}

      {!loading && error && (
        <div className={styles.error}>
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && agents.length === 0 && (
        <div className={styles.empty}>
          <Handshake size={28} className={styles.emptyIcon} />
          <p className={styles.emptyTitle}>No Sales Partners yet</p>
          <p className={styles.emptyBody}>
            Share a recruit link, or add a Sales Partner, to start attributing commissions on charger sales.
          </p>
        </div>
      )}

      {!loading && !error && agents.length > 0 && (
        <>
          <p className={dir.resultCount} aria-live="polite">
            {visible.length === agents.length
              ? `${agents.length} Sales Partner${agents.length === 1 ? '' : 's'}`
              : `${visible.length} of ${agents.length} Sales Partners`}
          </p>
          <ResponsiveTable
            columns={columns}
            rows={visible}
            rowKey={(a) => a.id}
            emptyMessage="No Sales Partners match the current search and filters."
          />
        </>
      )}

      {/* Notes for any partner, below the directory; each profile also
          carries its own notes on Details. */}
      {!loading && !error && <StaffPartnerNotes />}

      {/* Create modal */}
      {createOpen && (
        <AgentFormModal
          mode="create"
          onClose={() => setCreateOpen(false)}
          onSaved={async () => {
            setCreateOpen(false);
            await reload();
          }}
        />
      )}

      {/* Edit modal */}
      {editingAgent && (
        <AgentFormModal
          mode="edit"
          agent={editingAgent}
          onClose={() => { setEditingAgent(null); void reload(); }}
          onSaved={async () => {
            setEditingAgent(null);
            await reload();
          }}
        />
      )}

      {/* Copy invite link's dialog, outside the table so its clicks never reach a row. */}
      {inviteLink.dialog}

      {/* Confirm deactivate/reactivate */}
      {confirmAction && (
        <Modal
          isOpen
          onClose={() => {
            if (!confirmPending) {
              setConfirmAction(null);
              setConfirmErr('');
            }
          }}
          title={confirmAction.kind === 'deactivate' ? 'Deactivate Sales Partner?' : 'Reactivate Sales Partner?'}
          size="sm"
        >
          <div className={styles.confirmBody}>
            {confirmErr && <div className={formStyles.formError}>{confirmErr}</div>}
            {confirmAction.kind === 'deactivate' ? (
              <p>
                <strong>{confirmAction.agent.name}</strong> will be marked
                inactive. New sessions on chargers still pointing at this
                Sales Partner will skip the commission write. Existing
                payout history stays intact.
              </p>
            ) : (
              <p>
                Reactivate <strong>{confirmAction.agent.name}</strong>?
                New sessions on chargers pointing at this Sales Partner
                will start accruing commissions again.
              </p>
            )}
            <div className={styles.confirmActions}>
              <button
                type="button"
                className={formStyles.btnSecondary}
                onClick={() => setConfirmAction(null)}
                disabled={confirmPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className={confirmAction.kind === 'deactivate'
                  ? styles.btnDanger
                  : formStyles.btnPrimary}
                onClick={() => void handleToggleActive()}
                disabled={confirmPending}
              >
                {confirmPending
                  ? <><Loader2 size={12} className={styles.spin} /> Saving…</>
                  : confirmAction.kind === 'deactivate' ? 'Deactivate' : 'Reactivate'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
